// The Generate tab's working session (user, 2026-10-22): the photos, the
// settings, the prompt and the runs started from them survive leaving the
// tab and reloading the page, until the user presses Clear. Two layers:
//
//   - `memory` (module level) keeps everything for in-app navigation,
//     including File objects not yet uploaded and their blob previews;
//   - localStorage keeps what can outlive the page: uploaded photos by
//     artifact id, the settings, and the runs' task ids. A photo still
//     uploading when the page is reloaded is gone (its bytes were local).
//
// Both belong to one signed-in user: the storage key carries the user id,
// the memory copy its owner, and signing out forgets every session in this
// browser -- so the next person at the same browser never sees another
// user's photos, prompt or runs.
import type { UploadStep } from "./generate";

export interface SessionPhoto {
  index: number;
  name: string;
  artifactId: string;
}

export interface SessionRun {
  taskId: string;
  /** what was submitted, for "already ran exactly this" (runSignature) */
  signature: string;
  createdAt: string;
  /** "720p · 5 s · 24 fps · 2 results" */
  label: string;
  imageIds: string[];
  /** results the run will make (photos x variations); the Task's own
   * `requested` replaces it once read */
  results?: number;
}

export interface GenSettings {
  model: string;
  grade: string;
  seconds: number;
  fps: number;
  hint: string;
  variants: number;
}

export interface GenSession extends GenSettings {
  photos: SessionPhoto[];
  deselected: number[];
  runs: SessionRun[];
}

const PREFIX = "gpufan.generate.v1";
const keyFor = (owner: string) => `${PREFIX}:${owner}`;

export const memory: {
  owner?: string;
  steps?: UploadStep[];
  previews?: Record<number | string, string>;
  session?: GenSession;
} = {};

/** The in-memory copy for `owner`, or nothing (another user's is dropped). */
export function memoryFor(owner: string): typeof memory | undefined {
  if (memory.owner !== owner) return undefined;
  return memory;
}

export function loadSession(owner: string): GenSession | undefined {
  if (!owner) return undefined;
  if (memory.owner === owner && memory.session) return memory.session;
  try {
    const raw = window.localStorage.getItem(keyFor(owner));
    if (!raw) return undefined;
    const s = JSON.parse(raw) as GenSession;
    if (!Array.isArray(s.photos) || !Array.isArray(s.runs)) return undefined;
    return s;
  } catch {
    return undefined;
  }
}

export function saveSession(
  owner: string,
  s: GenSession,
  steps: UploadStep[],
  previews: Record<number | string, string>,
): void {
  if (!owner) return;
  if (memory.owner !== owner) forgetMemory();
  memory.owner = owner;
  memory.session = s;
  memory.steps = steps;
  memory.previews = previews;
  try {
    window.localStorage.setItem(keyFor(owner), JSON.stringify(s));
  } catch {
    /* private mode or full storage: the in-memory copy still serves navigation */
  }
}

function forgetMemory(): void {
  for (const url of Object.values(memory.previews ?? {})) {
    if (url.startsWith("blob:")) URL.revokeObjectURL(url);
  }
  memory.owner = undefined;
  memory.session = undefined;
  memory.steps = undefined;
  memory.previews = undefined;
}

/** Clear: this user's session, in memory and in storage. */
export function clearSession(owner?: string): void {
  forgetMemory();
  try {
    if (owner) window.localStorage.removeItem(keyFor(owner));
  } catch {
    /* nothing stored */
  }
}

/** Sign-out: every Generate session this browser holds, for any user
 * (including the unscoped key of the first release of this file). */
export function forgetAllSessions(): void {
  forgetMemory();
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && (k === PREFIX || k.startsWith(`${PREFIX}:`))) keys.push(k);
    }
    for (const k of keys) window.localStorage.removeItem(k);
  } catch {
    /* storage unavailable */
  }
}

/** The uploaded photos of a step list, for the stored session. */
export function sessionPhotos(steps: UploadStep[]): SessionPhoto[] {
  return steps
    .filter((s) => s.status === "done" && s.artifactId)
    .map((s) => ({ index: s.index, name: s.file.name, artifactId: s.artifactId as string }));
}

/** Steps for photos restored from storage: already uploaded, no bytes. */
export function restoredSteps(photos: SessionPhoto[]): UploadStep[] {
  return photos.map((p) => ({ index: p.index, file: new File([], p.name), status: "done" as const, artifactId: p.artifactId }));
}

/** Everything that makes two submissions the same run. Seeds are drawn
 * fresh each time, so they are not part of it: the same photos, model,
 * length, rate, prompt, enhance switch and variation count is "the same run
 * again". */
export function runSignature(p: {
  alias: string;
  imageIds: string[];
  seconds?: number;
  fps?: number;
  prompt: string;
  variants: number;
  enhance?: boolean;
  photoAt?: "start" | "middle" | "end";
}): string {
  const base: unknown[] = [p.alias, p.imageIds, p.seconds ?? null, p.fps ?? null, p.prompt.trim(), p.variants];
  if (p.enhance === false) base.push("raw");
  if (p.photoAt && p.photoAt !== "start") base.push("photo@" + p.photoAt);
  return JSON.stringify(base);
}

/** The next free photo index (indices stay stable when photos are removed). */
export function nextIndex(steps: UploadStep[]): number {
  return steps.reduce((m, s) => Math.max(m, s.index + 1), 0);
}
